/**
 * Basky's knowledge base — 20 curated Q&A pairs covering the Training Mgmt modules.
 * Each entry has:
 *   - id, module (label), keywords (lowercase tokens for matching)
 *   - question (display)
 *   - answer (markdown-lite — supports newlines + **bold** + lists)
 *   - links []   — deep-link chips rendered below the answer
 *   - sources [] — citation chips (doc library style)
 *   - live? boolean — if true, ask() fetches real data from the API
 */

export const KNOWLEDGE = [
  {
    id: 'recon-1', module: 'Reconciliation',
    question: 'How do I heal a mismatched invoice?',
    keywords: ['heal', 'reconcil', 'mismatch', 'invoice', 'drift', 'sync'],
    answer: `Reconciliation Auto-Heal is a 3-step flow:

1. **Open the Reconciliation Report** — lists all invoices where Books balance ≠ our DB balance (above tolerance ₹1).
2. **Click the ✨ Wand icon** on any row to fetch the latest Books state for that invoice. The row highlights green if healed.
3. **Review the Heal History** panel (📜 icon in the header) — shows before/after delta and who triggered it.

Finance teams use this to catch silent drift caused by manual Books edits that weren't synced.`,
    links: [{ label: 'Open Reconciliation', path: '/training/reconciliation' }],
    sources: ['Reconciliation Guide', 'Heal API docs'],
  },
  {
    id: 'no-crm-1', module: 'Students · CRM',
    question: 'What does the "No CRM" badge mean?',
    keywords: ['no crm', 'missing crm', 'badge', 'amber', 'salesperson'],
    answer: `"No CRM" means the student's **enrollment number is not set on any Zoho CRM Lead** — the salesperson forgot to fill the *Enrollment No* field on the Lead record.

**Why it matters**: the enrollment → lead mapping breaks, so you can't navigate from a student to their original lead context.

**How to fix**: ask the counsellor/salesperson to open the student in CRM and set the "Enrollment No." custom field to their Books enrollment number. Then hit the ⟳ refresh icon on the No CRM KPI card to re-check.`,
    links: [
      { label: 'View all No-CRM students', path: '/training/students?no_crm=1' },
    ],
    sources: ['Zoho CRM Lead mapping doc'],
  },
  {
    id: 'no-crm-live', module: 'Students · CRM', live: true, liveKey: 'no_crm_count',
    question: 'How many students are missing a CRM Lead?',
    keywords: ['how many', 'count', 'no crm', 'missing crm'],
    answer: '', // generated live
  },
  {
    id: 'anomaly-1', module: 'Anomalies',
    question: 'What is the Admission Pending rule?',
    keywords: ['admission pending', 'paid but no batch', 'anomaly', 'rule'],
    answer: `The **"Admission Pending"** rule flags students who:
- Paid **above the threshold** (default 30%) of their course fee
- Are **not assigned to any batch**
- Have **no applied credit notes** (so they're not refunded)

These are students who've financially committed but operations hasn't placed them. Resolve by either assigning a batch OR raising a ticket to the counsellor.`,
    links: [{ label: 'Open Anomalies', path: '/training/anomalies' }],
    sources: ['Anomaly Rules reference'],
  },
  {
    id: 'anomaly-2', module: 'Anomalies',
    question: 'How do I resolve an anomaly?',
    keywords: ['resolve', 'anomaly', 'close', 'comment'],
    answer: `Each anomaly row has a **Resolve** button (green checkmark). Click it, add a comment explaining what was done, and submit.

If the issue needs cross-team attention, use **Raise Ticket** instead — it assigns someone to own the fix and tracks closure time (see Ticket Performance).`,
    links: [
      { label: 'Open Anomalies', path: '/training/anomalies' },
      { label: 'Ticket Performance', path: '/training/tickets/performance' },
    ],
    sources: ['Anomaly resolution flow'],
  },
  {
    id: 'bookmark-1', module: 'Audit Bookmarks',
    question: 'What is an Audit Bookmark?',
    keywords: ['bookmark', 'audit', 'flag', 'category'],
    answer: `An Audit Bookmark is a **persistent flag** on a student. It tracks issues that need follow-up — separate from Tickets (which are one-time actions).

Bookmarks support:
- **Multi-category tagging** (Overdue, Attendance, Refund, Dropout, No CRM, etc.)
- **Priority** (Low / Medium / High)
- **Comment thread** with @mentions
- Status: Open / Resolved

Click the 🔖 icon next to any student name to create one.`,
    links: [{ label: 'Open Audit Bookmarks', path: '/training/audit-bookmarks' }],
    sources: ['Audit Bookmark guide'],
  },
  {
    id: 'bookmark-live', module: 'Audit Bookmarks', live: true, liveKey: 'bookmark_count',
    question: 'How many open audit bookmarks do I have?',
    keywords: ['how many', 'count', 'bookmark', 'open'],
    answer: '',
  },
  {
    id: 'ticket-1', module: 'Tickets',
    question: 'How are Tickets different from Audit Bookmarks?',
    keywords: ['ticket', 'bookmark', 'difference', 'when to use'],
    answer: `**Tickets** = one-shot actionable tasks assigned to a specific person with resolve/reopen lifecycle.
**Audit Bookmarks** = persistent flags that can stay open indefinitely, multi-category, comment thread.

Rule of thumb:
- Need someone else to **do something** → Ticket
- Need to **remember to check later** → Bookmark
- Both work together: a bookmark can spawn multiple tickets over time.`,
    sources: ['Tickets vs Bookmarks playbook'],
  },
  {
    id: 'ticket-2', module: 'Tickets',
    question: 'How is SLA measured in Ticket Performance?',
    keywords: ['sla', 'performance', 'ticket', 'target', '48h'],
    answer: `SLA target is **≤ 48 hours** from ticket creation to resolution.

**Ticket Performance** page shows per-assignee:
- Open / Resolved counts
- Avg · Median · P90 resolution time
- **SLA %** — share of tickets closed under 48h
- 14-day daily velocity sparkline
- Oldest open ticket age
- Status band: Excellent / On Track / Watch / At Risk`,
    links: [{ label: 'Open Ticket Performance', path: '/training/tickets/performance' }],
    sources: ['Performance metrics guide'],
  },
  {
    id: 'credit-1', module: 'Credit Notes',
    question: 'What are Credit Notes used for?',
    keywords: ['credit note', 'refund', 'write off', 'credits'],
    answer: `Credit Notes are **Zoho-synced records** of credits applied against student invoices — typically used for:
- Refunds (partial or full)
- Course switches
- Goodwill waivers

Each student's total credit shows on the Students KPI strip. Rows with credits applied get the **Has Credit** filter chip and show up with amber credit amount highlighted in the Credit column.`,
    links: [{ label: 'Open Credit Notes', path: '/training/credit-notes' }],
    sources: ['Credit Note sync doc'],
  },
  {
    id: 'custom-1', module: 'Custom Views',
    question: 'What are Custom Views?',
    keywords: ['custom view', 'saved filter', 'percentage', 'cross-field'],
    answer: `Custom Views are **saved filters with cross-field math** — inspired by Zoho CRM's saved filters.

You can express queries like:
- *"Students where paid_amount / total_amount < 50%"*
- *"Batches where student_count > 30 AND trainer = X"*
- *"Credit notes where amount > ₹5,000 in last 30 days"*

Each view can be shared across the team and pinned to the sidebar.`,
    links: [{ label: 'Open Custom Views', path: '/training/custom-views' }],
    sources: ['Custom Views feature doc'],
  },
  {
    id: 'batch-1', module: 'Batch Management',
    question: 'How do I assign a student to a batch?',
    keywords: ['assign', 'batch', 'enroll', 'student to batch'],
    answer: `Two paths:

**From Students page** → click the "Assign Batch" button on any row → pick the batch from the dropdown → confirm.

**From Batch Management** → open a batch → Students tab → click "Add Student" → search by name or enrollment.

Both create an **enrollment** record and update the student's "No Batch" status automatically.`,
    links: [
      { label: 'Students Page', path: '/training/students' },
      { label: 'Batch Management', path: '/training/batches' },
    ],
    sources: ['Enrollment flow'],
  },
  {
    id: 'report-1', module: 'Reports',
    question: 'What reports are available?',
    keywords: ['report', 'reports', 'analytics', 'dashboard', 'available'],
    answer: `The Reports section includes:
- **Operations Report** — centre/course breakdowns
- **Defaulters Report** — overdue fees by aging bucket
- **Collections Report** — day-wise collection trends
- **Zoho Reports Hub** — embedded Zoho Books/CRM dashboards with role-based access

Sales, Inside Sales, and Finance each see their own curated category.`,
    links: [
      { label: 'Reports', path: '/training/reports' },
      { label: 'Zoho Reports Hub', path: '/training/zoho-reports' },
    ],
    sources: ['Reports catalog'],
  },
  {
    id: 'sync-1', module: 'Zoho Sync',
    question: 'How often does Zoho data sync?',
    keywords: ['sync', 'zoho', 'frequency', 'refresh', 'update'],
    answer: `Two sync types run:

**Books Sync** — pulls invoices, payments, credit notes. Incremental (by last_modified_time). Triggered manually from Students page "Sync from Zoho" button, or scheduled hourly.

**CRM Sync** — pulls batches from CustomModule22 + Lead-link mappings. Also manual or scheduled.

The sync status chip in the header tells you the last successful sync time per source.`,
    links: [{ label: 'Sync Settings', path: '/training/sync-settings' }],
    sources: ['Zoho Sync architecture'],
  },
  {
    id: 'mention-1', module: 'Collaboration',
    question: 'How do @mentions work?',
    keywords: ['mention', '@', 'tag', 'notify', 'teammate'],
    answer: `Type **@** in any Audit Bookmark comment or Ticket comment → a typeahead picker appears with your teammates. Select one with Enter/Tab/click.

The mentioned user gets a **notification in the bell** 🔔 (top-right) with a deep-link back to the specific comment. Mentioned names render as clickable violet chips.`,
    sources: ['Notification & Mention spec'],
  },
  {
    id: 'live-tickets', module: 'Tickets', live: true, liveKey: 'open_tickets',
    question: 'How many open tickets are assigned to me?',
    keywords: ['my tickets', 'assigned to me', 'open tickets', 'how many tickets'],
    answer: '',
  },
  {
    id: 'perf-1', module: 'Ticket Performance',
    question: 'Who is the top performer this week?',
    keywords: ['top performer', 'leaderboard', 'best', 'fastest', 'who'],
    answer: `The **top performer** is the assignee with the highest SLA compliance %. Open the Ticket Performance page to see the live leaderboard — trophy icon marks #1.

Managers use the **At Risk banner** (red) to proactively reach out to assignees below 70% SLA before it becomes a review issue.`,
    links: [{ label: 'Ticket Performance', path: '/training/tickets/performance' }],
    sources: ['Performance metrics guide'],
  },
  {
    id: 'dashboard-1', module: 'Dashboard',
    question: 'What is on the main Dashboard?',
    keywords: ['dashboard', 'home', 'main page', 'overview'],
    answer: `The Dashboard is your daily glance: **Revenue today** · **New enrollments** · **Anomaly count** · **Overdue students** · **Upcoming batches** · **Recent activity feed**.

For deeper drill-downs, click any KPI card — it navigates to the filtered module view.`,
    links: [{ label: 'Dashboard', path: '/training/dashboard' }],
    sources: ['Dashboard layout spec'],
  },
  {
    id: 'write-off', module: 'Finance',
    question: 'What does "Write-off" mean?',
    keywords: ['write off', 'writeoff', 'bad debt', 'closed'],
    answer: `A write-off is a **formal closure of an unrecoverable due amount** — e.g., student stopped responding, refund was processed, or management approved a waiver.

In the app:
- Write-offs are synced from Zoho Books
- Students with write-offs get the "Written Off" filter chip
- The Write-off column appears in Students table (auto-hidden when filter = "Not Written Off")`,
    links: [{ label: 'Students · Written Off', path: '/training/students?written_off=1' }],
    sources: ['Write-off policy'],
  },
  {
    id: 'onboarding', module: 'General',
    question: 'Im new — where do I start?',
    keywords: ['new', 'start', 'onboard', 'first time', 'beginner', 'help'],
    answer: `Welcome! Here's the 5-minute tour:

1. **Dashboard** → glance at today's numbers
2. **Students** → core entity, everything revolves around this
3. **Anomalies** → rule-based issue detection
4. **Audit Bookmarks + Tickets** → two ways to track follow-ups (see "Tickets vs Bookmarks")
5. **Reconciliation** → keep Zoho Books data clean

Ask me about any module by name for a deeper dive.`,
    links: [
      { label: 'Dashboard', path: '/training/dashboard' },
      { label: 'Students', path: '/training/students' },
    ],
    sources: ['Onboarding checklist'],
  },
];

/** Context-specific starter prompts keyed by current pathname prefix. */
export const STARTERS_BY_PATH = {
  '/training/students': [
    'What does the "No CRM" badge mean?',
    'How many students are missing a CRM Lead?',
    'How do I assign a student to a batch?',
    'What does "Write-off" mean?',
    'What are Custom Views?',
  ],
  '/training/anomalies': [
    'What is the Admission Pending rule?',
    'How do I resolve an anomaly?',
    'How are Tickets different from Audit Bookmarks?',
    'How many open audit bookmarks do I have?',
    'Im new — where do I start?',
  ],
  '/training/reconciliation': [
    'How do I heal a mismatched invoice?',
    'How often does Zoho data sync?',
    'What are Credit Notes used for?',
    'Who is the top performer this week?',
    'What reports are available?',
  ],
  '/training/tickets': [
    'How is SLA measured in Ticket Performance?',
    'How many open tickets are assigned to me?',
    'Who is the top performer this week?',
    'How do @mentions work?',
    'How are Tickets different from Audit Bookmarks?',
  ],
  '/training/audit-bookmarks': [
    'What is an Audit Bookmark?',
    'How do @mentions work?',
    'How are Tickets different from Audit Bookmarks?',
    'How many open audit bookmarks do I have?',
    'Im new — where do I start?',
  ],
  default: [
    'Im new — where do I start?',
    'How do I heal a mismatched invoice?',
    'What does the "No CRM" badge mean?',
    'What is the Admission Pending rule?',
    'How is SLA measured in Ticket Performance?',
  ],
};

/** Very simple token-based scoring (no ML) — good enough for a prototype. */
function scoreMatch(query, entry) {
  const q = query.toLowerCase();
  let score = 0;
  // Direct substring on question gets big bonus
  if (entry.question.toLowerCase() === q) return 100;
  if (entry.question.toLowerCase().includes(q) && q.length > 5) score += 40;
  // Keyword overlap
  for (const kw of entry.keywords || []) {
    if (q.includes(kw)) score += 10 + kw.length * 0.5;
  }
  // Module name hit
  if (q.includes(entry.module.toLowerCase())) score += 6;
  return score;
}

export function findAnswer(query) {
  if (!query || !query.trim()) return null;
  const scored = KNOWLEDGE
    .map(e => ({ entry: e, score: scoreMatch(query, e) }))
    .filter(s => s.score > 5)
    .sort((a, b) => b.score - a.score);
  return scored[0]?.entry || null;
}

/** Suggested follow-ups based on the current entry's module. */
export function suggestedNext(entry, max = 3) {
  if (!entry) return [];
  return KNOWLEDGE
    .filter(e => e.id !== entry.id && e.module === entry.module)
    .slice(0, max)
    .map(e => e.question);
}

export function getStarters(pathname) {
  for (const prefix of Object.keys(STARTERS_BY_PATH)) {
    if (pathname.startsWith(prefix) && prefix !== 'default') return STARTERS_BY_PATH[prefix];
  }
  return STARTERS_BY_PATH.default;
}
